import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CommunityEvent } from "@/types/rider";
import { EventCard } from "./EventCard";

interface MobileEventSelectorProps {
  events: CommunityEvent[];
  selectedEventId: string | null;
  activeIndex: number;
  onEventSelect: (eventId: string, index: number) => void;
  onContinue: (selectedAddOns?: any[], totalPrice?: number) => void;
  onManualEntry: () => void;
  onPrevCard?: () => void;
  onNextCard?: () => void;
}

export const MobileEventSelector = ({
  events,
  selectedEventId,
  activeIndex,
  onEventSelect,
  onContinue,
  onManualEntry,
  onPrevCard,
  onNextCard,
}: MobileEventSelectorProps) => {
  const navigate = useNavigate();
  const selectedEvent = events.find(event => event.id === selectedEventId);
  
  // Add-ons state for mobile (same as desktop)
  const [addOns, setAddOns] = useState([
    {
      id: '1',
      description: 'Ship Wrek @ The Grand After Party',
      price: 0,
      enabled: false
    },
    {
      id: '2', 
      description: 'DJ Diesel @ Big Night Live After Party',
      price: 0,
      enabled: false
    },
    {
      id: '3',
      description: 'Professional Photography Service',
      price: 0,
      enabled: false
    }
  ]);

  const basePrice = selectedEvent?.price || 1200;
  const totalPrice = basePrice + addOns.filter(addon => addon.enabled && addon.price > 0).reduce((sum, addon) => sum + addon.price, 0);

  const toggleAddOn = (id: string) => {
    setAddOns(prev => prev.map(addon => 
      addon.id === id ? { ...addon, enabled: !addon.enabled } : addon
    ));
  };

  return (
    <div className="md:hidden bg-white text-black pb-24">
      <div className="flex justify-between items-center mb-8">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-black hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="mr-1 h-5 w-5" />
          <span>Events Near You</span>
        </button>
        <div></div>
      </div>

      <h1 className="text-3xl font-bold text-center mb-4 text-black">Book A Party Bus To</h1>
      
      <AnimatePresence mode="wait">
        {!selectedEventId ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col items-center"
          >
            <div className="relative w-full h-[450px] mb-8 flex items-center justify-center">
              {events.map((event, index) => {
                const offset = index - activeIndex;
                const isActive = index === activeIndex;
                
                if (Math.abs(offset) > 2) return null;
                
                return (
                  <motion.div
                    key={event.id}
                    className="absolute w-[85%] max-w-[330px]"
                    style={{
                      zIndex: events.length - Math.abs(offset),
                    }}
                    animate={{
                      y: offset * 10,
                      x: offset * 10,
                      rotate: offset * 3,
                      scale: 1 - Math.abs(offset) * 0.08,
                      opacity: 1 - Math.abs(offset) * 0.15,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 20
                    }}
                    onClick={() => onEventSelect(event.id, index)}
                  >
                    <EventCard
                      event={event}
                      isSelected={false}
                      onClick={() => {}}
                    />
                  </motion.div>
                );
              })}
            </div>
            
            {/* Navigation buttons */}
            <motion.div 
              className="flex space-x-4 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <button 
                className="bg-black text-white p-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onPrevCard}
                disabled={activeIndex === 0}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button 
                className="bg-black text-white p-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onNextCard}
                disabled={activeIndex === events.length - 1}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </motion.div>
            
            <motion.p 
              className="text-xl text-center mb-6 text-black font-semibold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Select An Event To See Pricing
            </motion.p>
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-gray-600 mb-2">Or</p>
              <button 
                onClick={onManualEntry}
                className="text-gray-600 underline hover:text-black transition-colors"
              >
                Enter Trip Request Manually
              </button>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex flex-col items-center"
          >
            {selectedEvent && (
              <>
                <motion.div 
                  className="w-full rounded-2xl overflow-hidden mb-4 h-[300px]"
                  layoutId={`event-card-${selectedEvent.id}`}
                >
                  <EventCard
                    event={selectedEvent}
                    isSelected={false}
                    onClick={() => {}}
                  />
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="w-full px-4"
                >
                  <h2 className="text-2xl font-bold mb-2 text-black">{selectedEvent.name}</h2>
                  <p className="text-gray-600 mb-6">{selectedEvent.startTime?.replace(' | ', ' - ').replace('Capacity', 'Up To').replace('Reservation', '').trim()}</p>
                  
                   <div className="flex justify-between items-center mb-6 border border-gray-200 rounded-lg p-4 bg-white">
                     <div>
                       <p className="text-2xl font-bold text-black">${totalPrice}</p>
                       <p className="text-gray-500">Price</p>
                     </div>
                     <div className="flex flex-col items-end">
                       <div className="flex">
                         {[...Array(5)].map((_, i) => (
                           <Star key={i} className="w-5 h-5 text-black" fill="currentColor" />
                         ))}
                       </div>
                       <p className="text-gray-500">5 Star Rating</p>
                     </div>
                   </div>
                   
                   {/* Add-ons Section for Mobile - Only show for Round Trip events */}
                   {selectedEvent.startTime?.includes('Round Trip') && (
                     <div className="mb-6 space-y-3">
                       {selectedEvent.date?.includes('Friday') ? (
                         <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                           <div className="flex-1">
                             <p className="text-black font-medium text-sm">Take Me To Ship Wrek @ The Grand After Party</p>
                             <p className="text-gray-600 text-xs">Free - $0</p>
                           </div>
                           <label className="relative inline-flex items-center cursor-pointer">
                             <input
                               type="checkbox"
                               className="sr-only peer"
                               checked={addOns[0]?.enabled || false}
                               onChange={() => toggleAddOn('1')}
                             />
                             <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                           </label>
                         </div>
                       ) : selectedEvent.date?.includes('Saturday') ? (
                         <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                           <div className="flex-1">
                             <p className="text-black font-medium text-sm">Take Me To DJ Diesel @ Big Night Live After Party</p>
                             <p className="text-gray-600 text-xs">Free - $0</p>
                           </div>
                           <label className="relative inline-flex items-center cursor-pointer">
                             <input
                               type="checkbox"
                               className="sr-only peer"
                               checked={addOns[1]?.enabled || false}
                               onChange={() => toggleAddOn('2')}
                             />
                             <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                           </label>
                         </div>
                       ) : null}
                     </div>
                   )}
                   
                   <div className="flex justify-center pb-8">
                     <Button 
                       className="w-full bg-white text-black border border-black hover:bg-black hover:text-white rounded-full py-6"
                       onClick={() => onContinue(addOns.filter(addon => addon.enabled), totalPrice)}
                     >
                       Book Today 🚌
                     </Button>
                   </div>
                </motion.div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
